import { FormEvent, ReactNode, useEffect, useMemo, useState } from "react";
import { Button, type ButtonProps } from "@/components/ui/button";
import { cn } from "@/lib/utils";
// Re-exported for backward compatibility. New call sites should import
// directly from `@/hooks/use-prefers-reduced-motion` to avoid pulling
// AuthScene's heavier deps (GoldParticles, Button, etc.) into their
// chunk if tree-shaking ever regresses.
export { usePrefersReducedMotion } from "@/hooks/use-prefers-reduced-motion";

// Tiny twinkling stars/sparkles. Each one has its own duration AND delay so
// they pulse out of sync — the scene reads as alive instead of frozen.
export function GoldParticles({ count = 28 }: { count?: number }) {
  const particles = useMemo(
    () =>
      Array.from({ length: count }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        top: 6 + Math.random() * 86,
        size: 1.2 + Math.random() * 2.6,
        delay: Math.random() * -10,
        duration: 4 + Math.random() * 7,
        opacity: 0.4 + Math.random() * 0.5,
        warm: Math.random() < 0.6,
      })),
    [count],
  );
  return (
    <div
      className="pointer-events-none absolute inset-0 z-0 overflow-hidden"
      aria-hidden
    >
      {particles.map((p) => {
        const color = p.warm ? "hsl(40, 90%, 78%)" : "hsl(40, 30%, 96%)";
        return (
          <span
            key={p.id}
            className="absolute rounded-full"
            style={{
              left: `${p.left}%`,
              top: `${p.top}%`,
              width: p.size,
              height: p.size,
              opacity: p.opacity,
              background: color,
              boxShadow: `0 0 ${p.size * 5}px ${color}`,
              animation: `authTwinkle ${p.duration}s ease-in-out ${p.delay}s infinite`,
            }}
          />
        );
      })}
    </div>
  );
}

// Slow drifting warm bokeh orbs — the kind of soft out-of-focus highlights
// you'd see in a cinematic storybook background.
export function BokehOrbs({ count = 6 }: { count?: number }) {
  const orbs = useMemo(
    () =>
      Array.from({ length: count }, (_, i) => {
        const warm = Math.random() < 0.7;
        return {
          id: i,
          left: 4 + Math.random() * 92,
          top: 8 + Math.random() * 78,
          size: 60 + Math.random() * 90,
          delay: Math.random() * -22,
          duration: 22 + Math.random() * 20,
          drift: (Math.random() - 0.5) * 80,
          rise: -(20 + Math.random() * 40),
          opacity: 0.10 + Math.random() * 0.16,
          color: warm ? "hsla(40, 95%, 72%, 1)" : "hsla(0, 0%, 100%, 1)",
        };
      }),
    [count],
  );
  return (
    <div
      className="pointer-events-none absolute inset-0 z-0 overflow-hidden"
      aria-hidden
    >
      {orbs.map((o) => (
        <span
          key={o.id}
          className="absolute rounded-full"
          style={
            {
              left: `${o.left}%`,
              top: `${o.top}%`,
              width: o.size,
              height: o.size,
              opacity: o.opacity,
              background: `radial-gradient(circle at 50% 50%, ${o.color} 0%, transparent 65%)`,
              filter: "blur(6px)",
              animation: `authBokehDrift ${o.duration}s ease-in-out ${o.delay}s infinite alternate`,
              ["--bokeh-x" as never]: `${o.drift}px`,
              ["--bokeh-y" as never]: `${o.rise}px`,
            } as React.CSSProperties
          }
        />
      ))}
    </div>
  );
}

// Rare gold "glints" — a tiny 4-point sparkle that brightens then fades, on
// a long staggered cycle so the eye catches one only every few seconds.
export function GoldGlints({ count = 4 }: { count?: number }) {
  const glints = useMemo(
    () =>
      Array.from({ length: count }, (_, i) => ({
        id: i,
        left: 6 + Math.random() * 88,
        top: 12 + Math.random() * 70,
        size: 10 + Math.random() * 14,
        delay: Math.random() * -18,
        duration: 9 + Math.random() * 9,
      })),
    [count],
  );
  return (
    <div
      className="pointer-events-none absolute inset-0 z-0 overflow-hidden"
      aria-hidden
    >
      {glints.map((g) => (
        <svg
          key={g.id}
          viewBox="0 0 24 24"
          width={g.size}
          height={g.size}
          className="absolute"
          style={{
            left: `${g.left}%`,
            top: `${g.top}%`,
            opacity: 0,
            filter: "drop-shadow(0 0 6px hsla(45, 100%, 70%, 0.9))",
            animation: `authGlint ${g.duration}s ease-in-out ${g.delay}s infinite`,
          }}
        >
          <path
            d="M12 1 L13 11 L23 12 L13 13 L12 23 L11 13 L1 12 L11 11 Z"
            fill="hsl(45, 100%, 80%)"
          />
        </svg>
      ))}
    </div>
  );
}

// Storybook background: deep cranberry sky, faint aurora-like wash, a hint
// of warm village light at the bottom edge — built entirely from gradients
// and a film-grain SVG noise overlay (no clipart).
export function StorybookBackdrop({ reduced }: { reduced: boolean }) {
  return (
    <div
      className="pointer-events-none absolute inset-0 z-0 overflow-hidden"
      aria-hidden
    >
      {/* Base wine sky — deeper top, almost-black bottom edge for depth */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(120% 80% at 50% 0%, hsl(354, 72%, 24%) 0%, hsl(355, 65%, 13%) 45%, hsl(354, 60%, 7%) 100%)",
        }}
      />
      {/* Soft aurora wash */}
      <div
        className="absolute inset-0 opacity-80 mix-blend-screen"
        style={{
          background:
            "radial-gradient(60% 40% at 50% 22%, hsla(0, 80%, 55%, 0.55) 0%, transparent 70%), radial-gradient(50% 35% at 18% 30%, hsla(354, 90%, 50%, 0.35) 0%, transparent 70%), radial-gradient(50% 35% at 82% 28%, hsla(354, 90%, 60%, 0.32) 0%, transparent 70%)",
          animation: reduced
            ? undefined
            : "authAurora 14s ease-in-out infinite alternate",
        }}
      />
      {/* Vertical light rays behind the heading area */}
      <div
        className="absolute inset-x-0 top-0 h-[70%] mix-blend-screen"
        style={{
          background:
            "conic-gradient(from 200deg at 50% -10%, transparent 0deg, hsla(40, 95%, 65%, 0.18) 12deg, transparent 28deg, transparent 50deg, hsla(0, 90%, 60%, 0.20) 65deg, transparent 85deg, transparent 110deg, hsla(40, 95%, 60%, 0.14) 130deg, transparent 150deg)",
          opacity: 0.55,
          filter: "blur(6px)",
          animation: reduced ? undefined : "authRays 18s ease-in-out infinite alternate",
          transformOrigin: "50% 0%",
        }}
      />
      {/* Warm gold haze halo behind heading */}
      <div
        className="absolute inset-x-0 top-[14%] h-[40%] mix-blend-screen"
        style={{
          background:
            "radial-gradient(45% 60% at 50% 30%, hsla(40, 95%, 60%, 0.32) 0%, hsla(40, 95%, 60%, 0.12) 35%, transparent 70%)",
          filter: "blur(2px)",
        }}
      />
      {/* Edge vignette for cinematic focus */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 95% 75% at center, transparent 45%, rgba(0,0,0,0.55) 90%, rgba(0,0,0,0.78) 100%)",
        }}
      />
      {/* Warm distant village glow at the bottom */}
      <div
        className="absolute inset-x-0 bottom-0 h-[42%]"
        style={{
          background:
            "radial-gradient(120% 60% at 18% 100%, hsla(38, 78%, 55%, 0.30) 0%, transparent 55%), radial-gradient(100% 60% at 80% 100%, hsla(38, 78%, 55%, 0.24) 0%, transparent 55%), radial-gradient(140% 50% at 50% 110%, hsla(38, 78%, 50%, 0.18) 0%, transparent 60%)",
          filter: "blur(28px)",
        }}
      />
      {/* Film-grain noise overlay */}
      <div
        className="absolute inset-0 opacity-[0.08] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E\")",
        }}
      />
    </div>
  );
}

// Faint, very blurred winter scenery anchored to the bottom corners — a few
// pine silhouettes, a hint of a cabin, and tiny warm window lights. Pure
// SVG + CSS so it stays light. Sits behind everything except the backdrop.
export function VillageSilhouettes() {
  return (
    <div
      className="pointer-events-none absolute inset-x-0 bottom-0 z-0 h-[55%] overflow-hidden"
      aria-hidden
    >
      {/* Left edge: cabin + pines */}
      <svg
        viewBox="0 0 360 220"
        preserveAspectRatio="xMinYMax meet"
        className="absolute bottom-0 left-0 h-[88%] w-[46%] max-w-[520px]"
        style={{ filter: "blur(3.5px)", opacity: 0.55 }}
      >
        {/* Pines back row */}
        <g fill="hsl(150, 35%, 6%)" opacity="0.85">
          <polygon points="20,220 60,120 100,220" />
          <polygon points="80,220 130,90 180,220" />
          <polygon points="160,220 200,140 240,220" />
        </g>
        {/* Cabin */}
        <g>
          <polygon points="220,200 270,150 320,200" fill="hsl(20, 30%, 10%)" />
          <rect x="232" y="195" width="76" height="35" fill="hsl(20, 35%, 12%)" />
          {/* Warm windows */}
          <rect x="244" y="206" width="14" height="14" fill="hsl(40, 95%, 65%)" opacity="0.95" />
          <rect x="282" y="206" width="14" height="14" fill="hsl(40, 95%, 65%)" opacity="0.95" />
        </g>
        {/* Pines front row */}
        <g fill="hsl(150, 38%, 4%)">
          <polygon points="0,220 50,140 100,220" />
          <polygon points="120,220 170,110 220,220" />
          <polygon points="190,220 240,160 290,220" />
          <polygon points="280,220 330,135 360,220" />
        </g>
        {/* Snow ground */}
        <rect x="0" y="210" width="360" height="14" fill="hsl(354, 50%, 14%)" opacity="0.8" />
      </svg>
      {/* Soft warm window glow on the left cabin */}
      <div
        className="absolute"
        style={{
          left: "16%",
          bottom: "10%",
          width: 160,
          height: 100,
          background:
            "radial-gradient(60% 60% at 50% 50%, hsla(40, 95%, 60%, 0.45) 0%, transparent 70%)",
          filter: "blur(8px)",
        }}
      />

      {/* Right edge: tall pines + small lit cabin hint */}
      <svg
        viewBox="0 0 360 220"
        preserveAspectRatio="xMaxYMax meet"
        className="absolute bottom-0 right-0 h-[92%] w-[46%] max-w-[520px]"
        style={{ filter: "blur(3.5px)", opacity: 0.55 }}
      >
        <g fill="hsl(150, 35%, 6%)" opacity="0.85">
          <polygon points="60,220 110,110 160,220" />
          <polygon points="180,220 220,140 260,220" />
          <polygon points="280,220 320,120 360,220" />
        </g>
        {/* Tiny cabin */}
        <g>
          <polygon points="40,205 70,170 100,205" fill="hsl(20, 30%, 10%)" />
          <rect x="48" y="200" width="48" height="22" fill="hsl(20, 35%, 12%)" />
          <rect x="58" y="206" width="9" height="9" fill="hsl(40, 95%, 65%)" opacity="0.95" />
          <rect x="80" y="206" width="9" height="9" fill="hsl(40, 95%, 65%)" opacity="0.95" />
        </g>
        <g fill="hsl(150, 38%, 4%)">
          <polygon points="0,220 50,150 100,220" />
          <polygon points="120,220 175,90 230,220" />
          <polygon points="220,220 275,130 330,220" />
          <polygon points="310,220 350,145 360,220" />
        </g>
        <rect x="0" y="210" width="360" height="14" fill="hsl(354, 50%, 14%)" opacity="0.8" />
      </svg>
      {/* Soft warm window glow on the right cabin */}
      <div
        className="absolute"
        style={{
          right: "30%",
          bottom: "12%",
          width: 130,
          height: 90,
          background:
            "radial-gradient(60% 60% at 50% 50%, hsla(40, 95%, 60%, 0.40) 0%, transparent 70%)",
          filter: "blur(8px)",
        }}
      />
    </div>
  );
}

export function JMonogram() {
  return (
    <div
      className="relative inline-flex h-14 w-14 items-center justify-center"
      aria-hidden
    >
      {/* Wreath ring */}
      <svg
        viewBox="0 0 64 64"
        className="absolute inset-0 h-full w-full"
        fill="none"
      >
        <defs>
          <radialGradient id="wreathGlow" cx="50%" cy="50%" r="50%">
            <stop offset="60%" stopColor="hsla(40, 95%, 75%, 0)" />
            <stop offset="100%" stopColor="hsla(40, 95%, 70%, 0.6)" />
          </radialGradient>
        </defs>
        <circle cx="32" cy="32" r="28" fill="url(#wreathGlow)" />
        <circle
          cx="32"
          cy="32"
          r="26"
          stroke="hsl(40, 85%, 70%)"
          strokeOpacity="0.85"
          strokeWidth="1"
        />
        {/* tiny sparkle dots around the ring */}
        {Array.from({ length: 8 }).map((_, i) => {
          const a = (i / 8) * Math.PI * 2 - Math.PI / 2;
          const x = 32 + Math.cos(a) * 26;
          const y = 32 + Math.sin(a) * 26;
          return (
            <circle
              key={i}
              cx={x}
              cy={y}
              r={i % 2 === 0 ? 0.9 : 0.5}
              fill="hsl(45, 95%, 80%)"
            />
          );
        })}
      </svg>
      <span
        className="font-serif text-[28px] font-semibold leading-none"
        style={{
          color: "hsl(45, 95%, 78%)",
          textShadow: "0 0 18px hsla(40, 95%, 65%, 0.65)",
        }}
      >
        J
      </span>
    </div>
  );
}

// Headline with the warm gold/red glow halo behind it. Pass `reduced` to
// disable the slow pulse for users who prefer reduced motion.
export function AuthHeadlineHalo({
  reduced,
  children,
}: {
  reduced: boolean;
  children: ReactNode;
}) {
  return (
    <div className="relative mt-3">
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-1/2 -z-0 h-[180%] w-[140%] -translate-x-1/2 -translate-y-1/2"
        style={{
          background:
            "radial-gradient(60% 50% at 50% 50%, hsla(40, 95%, 60%, 0.32) 0%, hsla(0, 80%, 55%, 0.10) 45%, transparent 75%)",
          filter: "blur(8px)",
          animation: reduced
            ? undefined
            : "authHeadlineGlow 7s ease-in-out infinite",
        }}
      />
      <h1
        className="relative text-center font-serif text-5xl font-medium leading-[1.05] tracking-tight sm:text-6xl md:text-7xl"
        style={{
          color: "hsl(40, 70%, 92%)",
          textShadow:
            "0 4px 30px hsla(40, 95%, 60%, 0.45), 0 2px 10px hsla(0, 80%, 55%, 0.45), 0 0 60px hsla(40, 95%, 65%, 0.18)",
        }}
      >
        {children}
      </h1>
    </div>
  );
}

// "Lit from within" form card. Wires up the gold edge ring, depth grain,
// top highlight, and inner warm glow so callers only need to drop their
// fields inside.
export function AuthCard({
  onSubmit,
  children,
  className,
}: {
  onSubmit: (e: FormEvent) => void;
  children: ReactNode;
  className?: string;
}) {
  return (
    <form
      onSubmit={onSubmit}
      className={cn(
        "relative mt-8 w-full max-w-[520px] rounded-[28px] border border-amber-200/30 bg-gradient-to-b from-[hsl(354,62%,20%)]/90 via-[hsl(354,65%,14%)]/88 to-[hsl(354,68%,9%)]/92 p-6 backdrop-blur-2xl sm:p-8",
        className,
      )}
      style={{
        boxShadow:
          "0 0 0 1px hsla(40, 95%, 65%, 0.18), 0 40px 90px -22px rgba(0,0,0,0.75), 0 12px 40px -12px hsla(0, 85%, 40%, 0.55), 0 0 60px -10px hsla(40, 90%, 55%, 0.20), inset 0 1px 0 hsla(40, 95%, 85%, 0.18), inset 0 -1px 0 hsla(0, 0%, 0%, 0.45)",
        // Subtle entrance lift: the card rises gently into place from 16px
        // below with a natural ease-out spring feel. 600ms is long enough
        // to read as intentional, short enough not to feel slow. `both`
        // fills backwards (opacity:0 before playback) so there's no flash.
        animation: "authCardEntrance 0.6s cubic-bezier(0.22, 1, 0.36, 1) both",
      }}
    >
      {/* gold edge glow ring */}
      <div
        aria-hidden
        className="pointer-events-none absolute -inset-px rounded-[28px]"
        style={{
          background:
            "linear-gradient(180deg, hsla(40, 95%, 72%, 0.55) 0%, hsla(0, 80%, 55%, 0.18) 30%, hsla(40, 95%, 70%, 0) 55%, hsla(40, 95%, 70%, 0.18) 80%, hsla(40, 95%, 75%, 0.40) 100%)",
          WebkitMask:
            "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
          WebkitMaskComposite: "xor",
          maskComposite: "exclude",
          padding: "1px",
        }}
      />
      {/* subtle inner depth grain */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-[28px] opacity-[0.06] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='c'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23c)' opacity='0.6'/%3E%3C/svg%3E\")",
        }}
      />
      {/* refined top highlight */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-6 top-0 h-px"
        style={{
          background:
            "linear-gradient(90deg, transparent 0%, hsla(40, 95%, 85%, 0.55) 50%, transparent 100%)",
        }}
      />
      {/* inner warm glow — gives the card a "lit from within" feel */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-[28px]"
        style={{
          background:
            "radial-gradient(80% 60% at 50% 35%, hsla(0, 90%, 55%, 0.18) 0%, transparent 65%)",
        }}
      />

      {children}
    </form>
  );
}

// Polished red/gold submit button with the gradient, sparkle accents on
// either side, and inner top highlight baked in. Forwards every Button
// prop so callers can wire `disabled`, `data-testid`, etc.
export function AuthPrimaryButton({
  children,
  className,
  style,
  ...props
}: ButtonProps) {
  return (
    <Button
      type="submit"
      {...props}
      className={cn(
        "group/btn relative h-14 w-full overflow-hidden rounded-2xl border border-amber-200/35 text-base font-semibold tracking-wide text-amber-50 transition-all hover:border-amber-200/60 focus-visible:ring-2 focus-visible:ring-amber-200/80 focus-visible:ring-offset-2 focus-visible:ring-offset-[hsl(354,55%,12%)] disabled:opacity-60",
        className,
      )}
      style={{
        background:
          "linear-gradient(180deg, hsl(0, 85%, 55%) 0%, hsl(354, 80%, 40%) 55%, hsl(354, 78%, 30%) 100%)",
        boxShadow:
          "0 0 0 1px hsla(40, 95%, 70%, 0.30), 0 16px 34px -10px hsla(0, 85%, 40%, 0.70), 0 0 30px hsla(0, 95%, 55%, 0.55), 0 0 60px -10px hsla(40, 95%, 60%, 0.35), inset 0 1px 0 hsla(40, 95%, 85%, 0.45), inset 0 -10px 26px hsla(0, 90%, 22%, 0.55)",
        ...style,
      }}
    >
      {/* warm inner top highlight */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-3 top-0 h-px"
        style={{
          background:
            "linear-gradient(90deg, transparent, hsla(40, 95%, 90%, 0.7), transparent)",
        }}
      />
      <span
        aria-hidden
        className="pointer-events-none absolute left-5 top-1/2 -translate-y-1/2 text-amber-200/85"
        style={{ textShadow: "0 0 10px hsla(40, 95%, 65%, 0.7)" }}
      >
        ✦
      </span>
      <span
        aria-hidden
        className="pointer-events-none absolute right-5 top-1/2 -translate-y-1/2 text-amber-200/85"
        style={{ textShadow: "0 0 10px hsla(40, 95%, 65%, 0.7)" }}
      >
        ✦
      </span>
      <span className="relative">{children}</span>
    </Button>
  );
}

// Shared keyframes used by GoldParticles + StorybookBackdrop. Mount once at
// the top of any auth page that uses them.
export function AuthSceneKeyframes() {
  return (
    <style>{`
      @keyframes authAurora {
        0% { transform: translateY(-2%) scale(1); opacity: 0.85; }
        100% { transform: translateY(2%) scale(1.04); opacity: 1; }
      }
      @keyframes authTwinkle {
        0%, 100% { opacity: 0.2; transform: scale(0.85); }
        50% { opacity: 1; transform: scale(1.3); }
      }
      @keyframes authBokehDrift {
        0% { transform: translate(0, 0); }
        100% { transform: translate(var(--bokeh-x, 0px), var(--bokeh-y, 0px)); }
      }
      @keyframes authGlint {
        0%, 88%, 100% { opacity: 0; transform: scale(0.6) rotate(0deg); }
        92% { opacity: 1; transform: scale(1.25) rotate(20deg); }
        96% { opacity: 0.4; transform: scale(0.95) rotate(40deg); }
      }
      @keyframes authRays {
        0% { transform: rotate(-3deg) scale(1); opacity: 0.45; }
        100% { transform: rotate(3deg) scale(1.05); opacity: 0.65; }
      }
      @keyframes authHeadlineGlow {
        0%, 100% { opacity: 0.55; transform: scale(1); }
        50% { opacity: 0.85; transform: scale(1.04); }
      }
      @keyframes authCardEntrance {
        from { opacity: 0; transform: translateY(16px) scale(0.985); }
        to   { opacity: 1; transform: translateY(0)   scale(1); }
      }
      /* Brian's friendly wave — a gentle back-and-forth tilt anchored
         at his feet so the raised hand sweeps a small arc, like a real
         "hi there!". Combined with a tiny vertical bob to keep it from
         looking mechanical. */
      @keyframes brianWave {
        0%   { transform: rotate(-2.5deg) translateY(0); }
        25%  { transform: rotate(2.5deg)  translateY(-2px); }
        50%  { transform: rotate(-2deg)   translateY(0); }
        75%  { transform: rotate(2deg)    translateY(-1px); }
        100% { transform: rotate(-2.5deg) translateY(0); }
      }
      @media (prefers-reduced-motion: reduce) {
        [style*="authAurora"],
        [style*="authTwinkle"],
        [style*="authBokehDrift"],
        [style*="authGlint"],
        [style*="authRays"],
        [style*="authHeadlineGlow"],
        [style*="authCardEntrance"],
        [style*="brianWave"] {
          animation: none !important;
        }
      }
    `}</style>
  );
}
